import moment from 'moment';
import { DigitalMap, WelcomeMessageButton, WelcomeMessageRule } from 'models/digitalMap';
import { Itinerary, ItineraryItem } from 'models/itinerary';
import { DEBUG } from 'lib/util/debug';
import { useCurrentPosition } from 'hooks/useCurrentPosition';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useTranslation } from 'react-i18next';

// Sample itinerary text:
// Here's a potential itinerary for your visit to Nutmeg Park today:
//
// - **10:00 AM - 11:00 AM:** Start with a relaxing morning ride on the [Enchanted Carousel](<url>) to ease into the day with some classic family fun.
// - **11:00 AM - 12:00 PM:** Head over to the [Café](https://nutmegpark.book.stg.ntmg.com/VfJm2gRfVg3xwh8LIPPY63XYWXcqSPck1mtkmNrD/maps/42ce357e-2007-47fd-9c5a-bdee4451f15a?lng=ja-JP&pin=87caab26-db88-48b6-8062-798ffd97d48a&pind=DETAILS) for a mid-morning snack or drink. Enjoy their freshly baked pastries and coffee.
// - **12:00 PM - 1:00 PM:** Try the Bumper Cars for some interactive family fun and excitement.
// - **1:00 PM - 2:00 PM:** Enjoy lunch at one of the park's cafés or fast food outlets.
// - **2:00 PM - 2:30 PM:** Catch the first Character Show at the designated venue for an entertaining break.
// - **3:00 PM - 4:00 PM:** Go for a ride on the Sky Thrill coaster for a bit more excitement.
// - **4:00 PM - 5:00 PM:** Relax with a scenic [Sky View Ferris wheel](<url>) ride for panoramic views of the park.

export const parseItinerary = (text: string): Itinerary | null => {
  const lines = text.split('\n');
  const itineraryItems: ItineraryItem[] = [];

  for (const line of lines) {
    // Eat non-word characters at the beginning of the line then match a time-of-day with optional case-insensitive am/pm
    const timeOfDayMatch = line.replace(/^\W*/, '').match(/^(\d{1,2}:\d{2} ?[AP]M)/i);

    if (!timeOfDayMatch) {
      continue;
    }

    // Match a link in the format (https://XXX)
    const linkMatch = line.match(/\((https?:\/\/\S+)\)/);

    if (!linkMatch) {
      continue;
    }

    let timeOfDay = timeOfDayMatch[1].toUpperCase();

    // Convert AM/PM time to 24-hour format
    if (/[AP]M$/i.test(timeOfDay)) {
      const [time, period] = timeOfDay.split(/\s+/);
      const [hours, minutes] = time.split(':').map(Number);

      let militaryHours = hours;
      if (period.toUpperCase() === 'PM' && hours !== 12) {
        militaryHours += 12;
      } else if (period.toUpperCase() === 'AM' && hours === 12) {
        militaryHours = 0;
      }

      timeOfDay = `${militaryHours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}`;
    }

    const link = linkMatch[1];

    // Extract pin key from the 'pin' query parameter using URL and URLSearchParams
    const url = new URL(link);
    const pinKey = url.searchParams.get('pin');

    if (!pinKey) {
      continue;
    }

    itineraryItems.push({
      time: timeOfDay,
      pin_key: pinKey,
      duration: '',
      travel_time: '',
    });
  }

  if (itineraryItems.length === 0) {
    return null;
  }

  return {
    id: '',
    digital_map_id: '',
    title: '',
    date_itineraries: [
      {
        date: moment().format('YYYY-MM-DD'),
        items: itineraryItems,
      },
    ],
  };
};

export const getWelcomeMessageRule = (
  chatSettings: DigitalMap['chat_settings'] | undefined,
  currentPosition: { lat: number; lng: number } | null,
  visitCount: number,
  currentTime: string
): WelcomeMessageRule | null => {
  DEBUG.log('getWelcomeMessageRule', {
    hasChatSettings: !!chatSettings,
    enableWelcomeMessages: chatSettings?.enable_welcome_messages,
    rulesCount: chatSettings?.welcome_message_rules?.length,
    currentPosition,
    visitCount,
    currentTime,
  });

  if (!chatSettings?.enable_welcome_messages || !chatSettings.welcome_message_rules?.length) {
    DEBUG.log('getWelcomeMessageRule', 'No welcome messages enabled or no rules defined');
    return null;
  }

  const enabledRules = getEnabledRules(chatSettings);

  // Find the first rule whose condition is met
  const matchingRule = enabledRules.find((rule, index) => {
    const isMatch = evaluateWelcomeMessageCondition(
      rule.condition,
      currentPosition,
      visitCount,
      currentTime
    );
    DEBUG.log(`getWelcomeMessageRule`, {
      ruleIndex: index,
      conditionType: rule.condition.type,
      isMatch,
      messageText: rule.welcome_message_text,
    });
    return isMatch;
  });

  DEBUG.log('getWelcomeMessageRule', {
    foundMatchingRule: !!matchingRule,
    messageText: matchingRule?.welcome_message_text,
  });

  return matchingRule ?? null;
};

const evaluateWelcomeMessageCondition = (
  condition: DigitalMap['chat_settings']['welcome_message_rules'][0]['condition'],
  currentPosition: { lat: number; lng: number } | null,
  visitCount: number,
  currentTime: string
): boolean => {
  DEBUG.log('evaluateWelcomeMessageCondition', {
    type: condition.type,
    hasPrimitiveCondition: !!condition.primitive_condition,
    orConditionsCount: condition.or_conditions?.length,
    andConditionsCount: condition.and_conditions?.length,
  });

  switch (condition.type) {
    case 'NONE':
      DEBUG.log('evaluateWelcomeMessageCondition', 'NONE condition always returns true');
      return true;
    case 'PRIMITIVE':
      const primitiveResult = evaluatePrimitiveCondition(
        condition.primitive_condition,
        currentPosition,
        visitCount,
        currentTime
      );
      DEBUG.log('evaluateWelcomeMessageCondition', { primitiveResult });
      return primitiveResult;
    case 'OR':
      const orResult =
        condition.or_conditions?.some((cond) =>
          evaluatePrimitiveCondition(cond, currentPosition, visitCount, currentTime)
        ) ?? false;
      DEBUG.log('evaluateWelcomeMessageCondition', { orResult });
      return orResult;
    case 'AND':
      const andResult =
        condition.and_conditions?.every((cond) =>
          evaluatePrimitiveCondition(cond, currentPosition, visitCount, currentTime)
        ) ?? false;
      DEBUG.log('evaluateWelcomeMessageCondition', { andResult });
      return andResult;
  }
};

type PrimitiveCondition = DigitalMap['chat_settings']['welcome_message_rules'][0]['condition']['primitive_condition'];

const evaluatePrimitiveCondition = (
  condition: PrimitiveCondition,
  currentPosition: { lat: number; lng: number } | null,
  visitCount: number,
  currentTime: string
): boolean => {
  if (!condition) {
    DEBUG.log('evaluatePrimitiveCondition', 'No condition provided');
    return false;
  }

  DEBUG.log('evaluatePrimitiveCondition', {
    type: condition.type,
    hasProximity: !!condition.proximity,
    hasVisitCount: !!condition.visit_count,
    hasTimeOfDay: !!condition.time_of_day,
  });

  switch (condition.type) {
    case 'PROXIMITY':
      if (!currentPosition || !condition.proximity) {
        DEBUG.log('evaluatePrimitiveCondition', 'Missing position data for proximity check');
        return false;
      }
      const distance = calculateDistance(currentPosition, {
        lat: condition.proximity.latitude,
        lng: condition.proximity.longitude,
      });
      const isInRange = distance <= condition.proximity.radius_meters;
      DEBUG.log('evaluatePrimitiveCondition', {
        distance,
        radiusMeters: condition.proximity.radius_meters,
        isInRange,
      });
      return isInRange;

    case 'VISIT_COUNT':
      if (!condition.visit_count) {
        DEBUG.log('evaluatePrimitiveCondition', 'Missing visit count condition');
        return false;
      }
      const visitCountResult = evaluateOperator(
        visitCount,
        condition.visit_count.operator,
        condition.visit_count.value,
        condition.visit_count.min_value,
        condition.visit_count.max_value
      );
      DEBUG.log('evaluatePrimitiveCondition', {
        currentCount: visitCount,
        operator: condition.visit_count.operator,
        value: condition.visit_count.value,
        minValue: condition.visit_count.min_value,
        maxValue: condition.visit_count.max_value,
        result: visitCountResult,
      });
      return visitCountResult;

    case 'TIME_OF_DAY':
      if (!condition.time_of_day) {
        DEBUG.log('evaluatePrimitiveCondition', 'Missing time of day condition');
        return false;
      }

      // Ensure time_of_day.min_value and time_of_day.max_value are in 24-hour format
      const minValue = condition.time_of_day.min_value
        ? moment(condition.time_of_day.min_value, 'HH:mm').format('HH:mm')
        : '';
      const maxValue = condition.time_of_day.max_value
        ? moment(condition.time_of_day.max_value, 'HH:mm').format('HH:mm')
        : '';
      const timeResult = evaluateOperator(
        currentTime,
        condition.time_of_day.operator,
        condition.time_of_day.value,
        minValue,
        maxValue
      );
      DEBUG.log('evaluatePrimitiveCondition', {
        currentTime,
        operator: condition.time_of_day.operator,
        value: condition.time_of_day.value,
        minValue,
        maxValue,
        result: timeResult,
      });
      return timeResult;
  }
};

const calculateDistance = (
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number => {
  // Haversine formula implementation
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (point1.lat * Math.PI) / 180;
  const φ2 = (point2.lat * Math.PI) / 180;
  const Δφ = ((point2.lat - point1.lat) * Math.PI) / 180;
  const Δλ = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const evaluateOperator = (
  value: number | string,
  operator: string,
  compareValue?: number | string,
  minValue?: number | string,
  maxValue?: number | string
): boolean => {
  switch (operator) {
    case 'LT':
      return value < (compareValue ?? 0);
    case 'GT':
      return value > (compareValue ?? 0);
    case 'EQ':
      return value === compareValue;
    case 'LTE':
      return value <= (compareValue ?? 0);
    case 'GTE':
      return value >= (compareValue ?? 0);
    case 'BETWEEN':
      return value >= (minValue ?? 0) && value <= (maxValue ?? 0);
    default:
      return false;
  }
};

interface WelcomeMessageResult {
  message: string | null;
  buttons: WelcomeMessageButton[];
  isLoading: boolean;
}

// Visit count management
let hasIncrementedThisSession = false;

const getVisitCount = (digitalMapId: string): number => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const key = `visit_count_${digitalMapId}`;
  const count = localStorage.getItem(key);
  return count ? parseInt(count, 10) : 0;
};

const incrementVisitCount = (digitalMapId: string): number => {
  const key = `visit_count_${digitalMapId}`;
  const currentCount = getVisitCount(digitalMapId);
  const newCount = currentCount + 1;
  localStorage.setItem(key, newCount.toString());
  return newCount;
};

export const useVisitCount = () => {
  const digitalMap = useSelector(selectDigitalMap);
  const digitalMapId = digitalMap?.id ?? '';

  const [visitCount, setVisitCount] = React.useState<number>(() => {
    // Initialize from localStorage
    return getVisitCount(digitalMapId);
  });

  React.useEffect(() => {
    if (!digitalMapId) return;

    // Only increment if we haven't incremented in this session
    if (!hasIncrementedThisSession) {
      DEBUG.log('useVisitCount', {
        digitalMapId,
        currentCount: visitCount,
        hasIncremented: hasIncrementedThisSession,
      });

      const newCount = incrementVisitCount(digitalMapId);
      setVisitCount(newCount);
      hasIncrementedThisSession = true;

      DEBUG.log('useVisitCount', {
        digitalMapId,
        newCount,
        hasIncremented: hasIncrementedThisSession,
      });
    }
  }, [digitalMapId]);

  return visitCount;
};

export const useWelcomeMessage = (): WelcomeMessageResult => {
  const { currentPosition, error: locationError } = useCurrentPosition();
  const [permissionState, setPermissionState] = React.useState<PermissionState | null>(null);
  const digitalMap = useSelector(selectDigitalMap);
  const chatSettings = digitalMap?.chat_settings;
  const visitCount = useVisitCount();
  const { t } = useTranslation();

  const defaultButtons: WelcomeMessageButton[] = [
    {
      icon_url: '/static/images/map/blank_calendar_orange.svg',
      label: t('Create Schedule'),
      prompt_text: t('What should I include in your schedule?'),
    },
    {
      icon_url: '/static/images/map/ticket_green.svg',
      label: t('Book Activities'),
      prompt_text: t('What kind of activities are you interested in?'),
    },
    {
      icon_url: '/static/images/map/eye_recommend_yellow.svg',
      label: t('Recommend Restaurant'),
      prompt_text: t('What kind of food are you in the mood for?'),
    },
  ];

  const defaultMessage = t("Hi, I'm your AI concierge. How can I help you today?");

  const enabledRules = React.useMemo(() => {
    return getEnabledRules(chatSettings);
  }, [chatSettings]);

  // Check if any rules require location data
  const hasLocationDependentRules = React.useMemo(() => {
    if (!enabledRules) return false;

    return enabledRules.some((rule) => {
      if (rule.condition.type === 'PRIMITIVE') {
        return rule.condition.primitive_condition?.type === 'PROXIMITY';
      }
      if (rule.condition.type === 'OR') {
        return rule.condition.or_conditions?.some((cond) => cond.type === 'PROXIMITY');
      }
      if (rule.condition.type === 'AND') {
        return rule.condition.and_conditions?.some((cond) => cond.type === 'PROXIMITY');
      }
      return false;
    });
  }, [enabledRules]);

  // Check location permission status
  React.useEffect(() => {
    if (!hasLocationDependentRules) {
      setPermissionState('granted'); // No need to check permissions
      return;
    }

    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      setPermissionState(result.state);

      // Listen for permission changes
      result.addEventListener('change', () => {
        setPermissionState(result.state);
      });
    });
  }, [hasLocationDependentRules]);

  // Determine if we should show loading state
  const isLoading = React.useMemo(() => {
    // No loading if there are no location dependent rules
    if (!hasLocationDependentRules) return false;

    // No loading if permission is denied
    if (permissionState === 'denied') return false;

    // No loading if we have the position
    if (currentPosition) return false;

    // Show loading if we're waiting for permission or position
    return permissionState === 'prompt' || !permissionState;
  }, [hasLocationDependentRules, permissionState, currentPosition]);

  // Evaluate rules after mount to prevent hydration errors
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  // Only evaluate rule after mount if it depends on client-only data
  const rule = React.useMemo(() => {
    if (!hasMounted) return null; // or return a default/fallback
    if (isLoading) return null;

    const currentTime = moment().format('HH:mm');
    const position = currentPosition
      ? {
          lat: currentPosition.lat(),
          lng: currentPosition.lng(),
        }
      : null;

    return getWelcomeMessageRule(chatSettings, position, visitCount, currentTime);
  }, [hasMounted, isLoading, chatSettings, currentPosition, visitCount]);

  const message = rule?.welcome_message_text ?? defaultMessage;
  const buttons = rule?.buttons ?? defaultButtons;

  DEBUG.log('useWelcomeMessage', {
    hasLocationDependentRules,
    permissionState,
    locationError: locationError?.message,
    hasPosition: !!currentPosition,
    isLoading,
    message,
  });

  return { message, buttons, isLoading };
};

const getEnabledRules = (chatSettings?: DigitalMap['chat_settings']): WelcomeMessageRule[] => {
  if (!chatSettings?.welcome_message_rules || !chatSettings.enable_welcome_messages) return [];

  return chatSettings.welcome_message_rules.filter((rule) => rule.status !== 'DISABLED');
};
